import * as React from "react";
import Grid from "@mui/material/Grid";
import Title from "@/components/molecules/Title";
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import {
  DriverWorkShift,
  OperationPlan,
  OperationPlanError,
} from "@/models/operationPlans";
import { useParams } from "react-router-dom";
import {
  downtimeType,
  downtimeTypeForEditUser,
  duplicateTime,
  findShift,
  getTimes,
  holidayFlags,
  isEqualPlan,
  OperationPlansConstansts,
  shiftToTime,
  workingHours,
  workingMins,
} from "./OperationPlansConstansts";
import TextField from "@/components/atoms/TextField";
import { Autocomplete } from "@/components/molecules/AutoComplete";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import {
  getDriverWorkShiftsAPI,
  getOperationPlanAPI,
  updateOperationPlanAPI,
} from "@/util/api/operationPlans";
import { Messages } from "@/util/Messages";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { AxiosError } from "axios";
import { getLoginUserAPI } from "@/util/api/API";
import { GX_ADMIN } from "@/models/auth";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";

export const OperationPlansEditPage = () => {
  const { navigate } = useStatefulNavigator();
  const [gxAdmin, setGxAdmin] = React.useState(false);
  const [data, setData] = React.useState<OperationPlan>();
  const [refData, setRefData] = React.useState<OperationPlan>();
  const [workingShifts, setWorkingShifts] = React.useState<DriverWorkShift[]>(
    []
  );
  const [error, setError] = React.useState<OperationPlanError>({});

  const params = useParams<{ id: string; date_id: string }>();
  const id = params.id;

  const [disableShifts, setDisableShifts] = React.useState(true);

  const [loading, updateOperationPlanAPIWrapper] = useLoadingPromise(
    updateOperationPlanAPI
  );

  const generateShiftHandler = React.useCallback(
    (sec: "front" | "back") => {
      return (
        e: React.SyntheticEvent<Element, Event>,
        value: DriverWorkShift | null | undefined
      ) => {
        if (!data) {
          return;
        }

        const { fromH, fromM, toH, toM } = shiftToTime(
          data.isHoliday,
          value ?? undefined
        );

        setError({
          ...error,
          [`${sec}DriverWorkStartHours`]: false,
          [`${sec}DriverWorkStartMinutes`]: false,
          [`${sec}DriverWorkEndHours`]: false,
          [`${sec}DriverWorkEndMinutes`]: false,
        });

        setData({
          ...data,
          [`${sec}DriverWorkShiftId`]: value?.id,
          [`${sec}DriverWorkShifts`]: value ?? undefined,
          [`${sec}DriverWorkStartHours`]: fromH,
          [`${sec}DriverWorkStartMinutes`]: fromM,
          [`${sec}DriverWorkEndHours`]: toH,
          [`${sec}DriverWorkEndMinutes`]: toM,
        });
      };
    },
    [data, error]
  );

  const handleChangeFrontShifts = React.useMemo(
    () => generateShiftHandler("front"),
    [generateShiftHandler]
  );

  const handleChangeBackShifts = React.useMemo(
    () => generateShiftHandler("back"),
    [generateShiftHandler]
  );

  const handleChangeHolidayFlag = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, value: string | null) => {
      if (!data) {
        return;
      }

      const nextData = {
        ...data,
        isHoliday: value === "土日",
      };

      const {
        fromH: frontDriverWorkStartHours,
        fromM: frontDriverWorkStartMinutes,
        toH: frontDriverWorkEndHours,
        toM: frontDriverWorkEndMinutes,
      } = getTimes(nextData, "front");

      const {
        fromH: backDriverWorkStartHours,
        fromM: backDriverWorkStartMinutes,
        toH: backDriverWorkEndHours,
        toM: backDriverWorkEndMinutes,
      } = getTimes(nextData, "back");

      setData({
        ...nextData,
        frontDriverWorkStartHours,
        frontDriverWorkStartMinutes,
        frontDriverWorkEndHours,
        frontDriverWorkEndMinutes,
        backDriverWorkStartHours,
        backDriverWorkStartMinutes,
        backDriverWorkEndHours,
        backDriverWorkEndMinutes,
      });
    },
    [data]
  );

  const handleChangeDowntimeFlag = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, value: string | null) => {
      if (!refData || !data) {
        return;
      }

      // 休車を選んだ場合は、非活性になる項目を初期表示に戻す
      const stopCarType =
        value === "休車"
          ? gxAdmin
            ? "0"
            : refData.stopCarType ?? "0"
          : value === "休車（請求なし）"
          ? "1"
          : undefined;

      setError({});
      setData({
        ...data,
        stopCarType,
        isHoliday: data.isHoliday,
      });
      setDisableShifts(["0", "1"].includes(stopCarType ?? ""));
    },
    [data, gxAdmin, refData]
  );

  const generateTimeHandler = React.useCallback(
    (
      sec1: "front" | "back",
      sec2: "Start" | "End",
      sec3: "Hours" | "Minutes"
    ) => {
      return (
        e: React.SyntheticEvent<Element, Event>,
        value: String | null | undefined
      ) => {
        if (!data) {
          return;
        }

        const nextData = {
          ...data,
          [`${sec1}DriverWork${sec2}${sec3}`]:
            value != null ? Number(value) : undefined,
        };

        const nextShift = findShift(workingShifts, nextData, sec1);
        setData({
          ...nextData,
          [`${sec1}DriverWorkShifts`]: nextShift,
          [`${sec1}DriverWorkShiftId`]: nextShift?.id,
        });
        setError({
          ...error,
          [`${sec1}DriverWork${sec2}${sec3}`]: false,
          [`${sec1}TimeDuplicate`]: false,
        });
      };
    },
    [data, error, workingShifts]
  );

  const handleChangeFrontStartHours = React.useMemo(
    () => generateTimeHandler("front", "Start", "Hours"),
    [generateTimeHandler]
  );
  const handleChangeFrontStartMinutes = React.useMemo(
    () => generateTimeHandler("front", "Start", "Minutes"),
    [generateTimeHandler]
  );
  const handleChangeFrontEndHours = React.useMemo(
    () => generateTimeHandler("front", "End", "Hours"),
    [generateTimeHandler]
  );
  const handleChangeFrontEndMinutes = React.useMemo(
    () => generateTimeHandler("front", "End", "Minutes"),
    [generateTimeHandler]
  );

  const handleChangeBackStartHours = React.useMemo(
    () => generateTimeHandler("back", "Start", "Hours"),
    [generateTimeHandler]
  );
  const handleChangeBackStartMinutes = React.useMemo(
    () => generateTimeHandler("back", "Start", "Minutes"),
    [generateTimeHandler]
  );
  const handleChangeBackEndHours = React.useMemo(
    () => generateTimeHandler("back", "End", "Hours"),
    [generateTimeHandler]
  );
  const handleChangeBackEndMinutes = React.useMemo(
    () => generateTimeHandler("back", "End", "Minutes"),
    [generateTimeHandler]
  );

  // Handle go to chargers list
  const goToOperationPlansList = React.useCallback(() => {
    navigate("/operation_plans");
  }, [navigate]);

  // Handle event submit
  const onSubmit = React.useCallback(() => {
    if (data == null || loading) {
      return;
    }

    const isStopped = ["0", "1"].includes(data.stopCarType ?? "");
    const isFrontDayOff =
      (data.isHoliday && !!data?.frontDriverWorkShifts?.holidayIsDayOff) ||
      (!data.isHoliday && !!data?.frontDriverWorkShifts?.weekdayIsDayOff);
    const isBackDayOff =
      (data.isHoliday && !!data?.backDriverWorkShifts?.holidayIsDayOff) ||
      (!data.isHoliday && !!data?.backDriverWorkShifts?.weekdayIsDayOff);

    const errorCurrent: OperationPlanError = {
      frontTimeDuplicate: duplicateTime(data, "front"),
      frontDriverWorkStartHours:
        !isStopped && !isFrontDayOff && data.frontDriverWorkStartHours == null,
      frontDriverWorkStartMinutes:
        !isStopped &&
        !isFrontDayOff &&
        data.frontDriverWorkStartMinutes == null,
      frontDriverWorkEndHours:
        !isStopped && !isFrontDayOff && data.frontDriverWorkEndHours == null,
      frontDriverWorkEndMinutes:
        !isStopped && !isFrontDayOff && data.frontDriverWorkEndMinutes == null,
      backTimeDuplicate: duplicateTime(data, "back"),
      backDriverWorkStartHours:
        !isStopped && !isBackDayOff && data.backDriverWorkStartHours == null,
      backDriverWorkStartMinutes:
        !isStopped && !isBackDayOff && data.backDriverWorkStartMinutes == null,
      backDriverWorkEndHours:
        !isStopped && !isBackDayOff && data.backDriverWorkEndHours == null,
      backDriverWorkEndMinutes:
        !isStopped && !isBackDayOff && data.backDriverWorkEndMinutes == null,
    };

    if (Object.values(errorCurrent).some((error) => error)) {
      setError(errorCurrent);
      return;
    }

    updateOperationPlanAPIWrapper(data)
      .then(() => goToOperationPlansList())
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([400, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${
              e?.response?.data?.message ?? ""
            }`
          );
        }

        console.log("===Error occurred while updating operation plan===", e);
      });
  }, [data, goToOperationPlansList, loading, updateOperationPlanAPIWrapper]);

  // Load charger detail when id change
  React.useEffect(() => {
    if (id == null) {
      return;
    }

    getLoginUserAPI().then((res) => setGxAdmin(GX_ADMIN === res?.data?.role));
    getOperationPlanAPI(Number(id))
      .then(({ data }) => {
        setData({ ...data });
        setRefData({ ...data });
        setDisableShifts(["0", "1"].includes(data?.stopCarType ?? ""));
        return data.officeId;
      })
      .then((officeId) => {
        if (officeId == null) {
          return;
        }

        getDriverWorkShiftsAPI(officeId)
          .then(({ data }) => setWorkingShifts(data))
          .catch((e) =>
            console.log(
              "===Error occurred while getting driver work shifts===",
              e
            )
          );
      })
      .catch((e) =>
        console.log("===Error occurred while getting operation plan===", 2)
      );
  }, [id]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{OperationPlansConstansts.OperationPlanEditing}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                size="small"
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                  backgroundColor: "#FFF",
                  mb: 2,
                  width: "60%",
                }}
                className="gx-info-table-no-width"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={OperationPlansConstansts.OfficeName}
                        value={data?.office?.displayName ?? ""}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ pl: 1, width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={OperationPlansConstansts.RadioNumber}
                        value={data?.vehicleCode ?? ""}
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ pl: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={OperationPlansConstansts.OperationPlanDate}
                        value={
                          data?.targetDate
                            ? new Date(data.targetDate)
                                .toISOString()
                                .slice(0, 10)
                                .replace(/-/g, "/")
                            : ""
                        }
                        InputProps={{
                          readOnly: true,
                          disableUnderline: true,
                        }}
                        sx={{ pl: 1 }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pt: 2 }}>
                      <Grid item container>
                        <Autocomplete
                          label={
                            OperationPlansConstansts.OperationPlanWeekdaysHolidays
                          }
                          sx={{ width: 195 }}
                          options={holidayFlags}
                          value={data?.isHoliday ? "土日" : "平日"}
                          onChange={handleChangeHolidayFlag}
                          disableClearable={true}
                          defaultValue={undefined}
                        />
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pt: 2 }}>
                      <Grid item container>
                        <Autocomplete
                          label={OperationPlansConstansts.TakeABreak}
                          sx={{ width: 195 }}
                          options={
                            gxAdmin ? downtimeType : downtimeTypeForEditUser
                          }
                          onChange={handleChangeDowntimeFlag}
                          value={
                            data?.stopCarType == null
                              ? "―"
                              : gxAdmin
                              ? data?.stopCarType === "0"
                                ? "休車"
                                : "休車（請求なし）"
                              : "休車"
                          }
                          disableClearable={true}
                          defaultValue={undefined}
                        />
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pt: 2 }}>
                      <Grid item display={"flex"} flexDirection={"row"}>
                        <Autocomplete
                          label={OperationPlansConstansts.WorkShiftTable}
                          sx={{ width: 195 }}
                          options={workingShifts}
                          value={data?.frontDriverWorkShifts}
                          onChange={handleChangeFrontShifts}
                          getOptionLabel={(opt) => opt?.shortName ?? ""}
                          isOptionEqualToValue={(a, b) =>
                            a?.shortName === b?.shortName
                          }
                          defaultValue={undefined}
                          disabled={disableShifts}
                        />
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Grid item>
                        <FormControl error={!!error.frontTimeDuplicate}>
                          <FormLabel sx={{ fontSize: "0.75rem" }}>
                            {OperationPlansConstansts.WorkShiftTableStartTime}
                          </FormLabel>
                          <FormHelperText
                            sx={{
                              margin: 0,
                              display: !error.frontTimeDuplicate
                                ? "none"
                                : undefined,
                            }}
                          >
                            {OperationPlansConstansts.SameTimeError}
                          </FormHelperText>
                          <Grid container item>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingHours}
                              value={
                                data?.frontDriverWorkStartHours
                                  ?.toString()
                                  .padStart(2, "0") ?? null
                              }
                              onChange={handleChangeFrontStartHours}
                              defaultValue={undefined}
                              disabled={disableShifts}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              error={!!error.frontDriverWorkStartHours}
                            />
                            <Typography
                              sx={{ alignSelf: "center" }}
                              component={"span"}
                            >
                              &nbsp;:&nbsp;
                            </Typography>
                            <Autocomplete
                              sx={{ width: 90 }}
                              options={workingMins}
                              value={
                                data?.frontDriverWorkStartMinutes
                                  ?.toString()
                                  .padStart(2, "0") ?? null
                              }
                              onChange={handleChangeFrontStartMinutes}
                              defaultValue={undefined}
                              disabled={disableShifts}
                              errorLabel={Messages.PleaseEnter.slice(1)}
                              error={!!error.frontDriverWorkStartMinutes}
                            />
                          </Grid>
                        </FormControl>
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormControl>
                        <FormLabel sx={{ fontSize: "0.75rem" }}>
                          {OperationPlansConstansts.WorkShiftTableEndTime}
                        </FormLabel>
                        <Grid container item>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingHours}
                            value={
                              data?.frontDriverWorkEndHours
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeFrontEndHours}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.frontDriverWorkEndHours}
                          />
                          <Typography
                            sx={{ alignSelf: "center" }}
                            component={"span"}
                          >
                            &nbsp;:&nbsp;
                          </Typography>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingMins}
                            value={
                              data?.frontDriverWorkEndMinutes
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeFrontEndMinutes}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.frontDriverWorkEndMinutes}
                          />
                        </Grid>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pt: 2 }}>
                      <Grid item display={"flex"} flexDirection={"row"}>
                        <Autocomplete
                          label={OperationPlansConstansts.WorkShiftBack}
                          sx={{ width: 195 }}
                          options={workingShifts}
                          value={data?.backDriverWorkShifts}
                          onChange={handleChangeBackShifts}
                          defaultValue={undefined}
                          getOptionLabel={(opt) => opt?.shortName ?? ""}
                          isOptionEqualToValue={(a, b) =>
                            a?.shortName === b?.shortName
                          }
                          disabled={disableShifts}
                        />
                      </Grid>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormControl error={!!error.backTimeDuplicate}>
                        <FormLabel sx={{ fontSize: "0.75rem" }}>
                          {OperationPlansConstansts.WorkShiftBackStartTime}
                        </FormLabel>
                        <FormHelperText
                          sx={{
                            margin: 0,
                            display: !error.backTimeDuplicate
                              ? "none"
                              : undefined,
                          }}
                        >
                          {OperationPlansConstansts.SameTimeError}
                        </FormHelperText>
                        <Grid container item>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingHours}
                            value={
                              data?.backDriverWorkStartHours
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeBackStartHours}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.backDriverWorkStartHours}
                          />
                          <Typography
                            sx={{ alignSelf: "center" }}
                            component={"span"}
                          >
                            &nbsp;:&nbsp;
                          </Typography>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingMins}
                            value={
                              data?.backDriverWorkStartMinutes
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeBackStartMinutes}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.backDriverWorkStartMinutes}
                          />
                        </Grid>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <FormControl>
                        <FormLabel sx={{ fontSize: "0.75rem" }}>
                          {OperationPlansConstansts.WorkShiftBackEndTime}
                        </FormLabel>
                        <Grid container item>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingHours}
                            value={
                              data?.backDriverWorkEndHours
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeBackEndHours}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.backDriverWorkEndHours}
                          />
                          <Typography
                            sx={{ alignSelf: "center" }}
                            component={"span"}
                          >
                            &nbsp;:&nbsp;
                          </Typography>
                          <Autocomplete
                            sx={{ width: 90 }}
                            options={workingMins}
                            value={
                              data?.backDriverWorkEndMinutes
                                ?.toString()
                                .padStart(2, "0") ?? null
                            }
                            onChange={handleChangeBackEndMinutes}
                            defaultValue={undefined}
                            disabled={disableShifts}
                            errorLabel={Messages.PleaseEnter.slice(1)}
                            error={!!error.backDriverWorkEndMinutes}
                          />
                        </Grid>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                loading={loading || isEqualPlan(data, refData)}
                onCancel={goToOperationPlansList}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
